import {
  faBan,
  faCircleCheck,
  faCircleXmark,
  IconDefinition,
} from '@fortawesome/pro-regular-svg-icons';
import { ZonedDateTime, ZoneId } from '@js-joda/core';
import groupBy from 'lodash/groupBy';
import {
  CoachSessionAttendance,
  CoachSessionBasicsFragment,
  CoachSessionFilter,
  CoachSessionStyle,
  CoachSessionSubtype,
  CoachSessionType,
} from '../../../graphql/types';
import { AQUAMARINE, PRIMARY_DISABLED, STATUS_ERROR } from '../../../theme';
import {
  monthDayYearTimeLongForm,
  monthYear,
  stringToZonedDateTime,
} from '../../../utils/date';
import { EventLabels, EventSubCategories } from '../../../utils/trackingEvents';
import { OptionType } from '../Select';

const coachingSessionLabel = 'Coaching Session';
const nutritionSessionLabel = 'Nutrition Session';
const ptSessionLabel = 'Personal Training Session';

const coachSessionLabelMap: Record<CoachSessionType, string> = {
  [CoachSessionType.UserCoachingConsult]: coachingSessionLabel,
  [CoachSessionType.CoachingSession]: coachingSessionLabel,
  [CoachSessionType.UserNutritionConsult]: nutritionSessionLabel,
  [CoachSessionType.PersonalTraining]: ptSessionLabel,
};

export const getCoachSessionLabel = (session: CoachSessionBasicsFragment) => {
  if (!session.sessionType) {
    return coachingSessionLabel;
  }

  return coachSessionLabelMap[session.sessionType];
};

export const coachSessionStyleMap: Record<CoachSessionStyle, string> = {
  [CoachSessionStyle.InPerson]: 'In Person',
  [CoachSessionStyle.Virtual]: 'Virtual',
};

export const formattedSessionDateTime = (
  session: CoachSessionBasicsFragment,
) => {
  if (!session.calendar?.start) {
    return null;
  }

  const zonedSessionTime = ZonedDateTime.parse(
    session.calendar.start,
  ).withZoneSameInstant(ZoneId.SYSTEM);

  return monthDayYearTimeLongForm(zonedSessionTime);
};

export const groupSessionsByMonth = (
  sessions: CoachSessionBasicsFragment[],
) => {
  const sessionsByMonth = groupBy(sessions, (session) => {
    if (!session.calendar?.start) {
      return null;
    }

    const parsedSessionTime = stringToZonedDateTime(session.calendar.start);

    if (parsedSessionTime.isAfter(ZonedDateTime.now())) {
      return 'Upcoming';
    }

    return monthYear(parsedSessionTime);
  });

  // don't include sessions without dates, this shouldn't happen but is safety
  // because of nullability in the GQL schema
  delete sessionsByMonth.null;
  return sessionsByMonth;
};

export const getCoachSessionCoachFilterOptions = (
  coachId?: string,
): OptionType<undefined | string>[] => [
  {
    label: 'All Coaches',
    value: undefined,
  },
  ...(coachId
    ? [
        {
          label: 'Only Me',
          value: coachId,
        },
      ]
    : []),
];

export const getCoachSessionTypeFilterOptions: (input: {
  ptEnabled?: boolean;
  nutritionEnabled?: boolean;
}) => OptionType<undefined | CoachSessionType>[] = ({
  ptEnabled = false,
  nutritionEnabled = false,
}) =>
  [
    {
      label: 'All Types',
      value: undefined,
    },
    {
      label: 'Coaching',
      value: CoachSessionType.UserCoachingConsult,
    },
    ptEnabled && {
      label: 'Personal Training',
      value: CoachSessionType.PersonalTraining,
    },
    nutritionEnabled && {
      label: 'Nutrition',
      value: CoachSessionType.UserNutritionConsult,
    },
  ].filter((option) => !!option) as OptionType<undefined | CoachSessionType>[];

export const getCoachSessionAttendanceFilterOptions = ({
  usesPersonalTraining = false,
}: {
  usesPersonalTraining?: boolean;
}): OptionType<CoachSessionAttendance | null | undefined>[] => [
  {
    label: 'All Attendance',
    value: undefined,
  },
  {
    label: 'Attended',
    value: CoachSessionAttendance.Attended,
  },
  {
    label: 'No Show',
    value: CoachSessionAttendance.NoShow,
  },
  ...(usesPersonalTraining
    ? [
        {
          label: 'Excused Absence',
          value: CoachSessionAttendance.Excused,
        },
      ]
    : []),
  {
    label: 'Not Recorded',
    value: null,
  },
];

export const defaultCoachSessionFilters = {
  coachId: getCoachSessionCoachFilterOptions()[0],
  sessionType: getCoachSessionTypeFilterOptions({})[0],
  attendance: getCoachSessionAttendanceFilterOptions({})[0],
};

export const coachSessionFilterFieldToTrackingLabelMap: Record<
  keyof CoachSessionFilter,
  EventSubCategories
> = {
  coachId: EventSubCategories.Coach,
  sessionType: EventSubCategories.Type,
  attendance: EventSubCategories.Attendance,
  attendances: EventSubCategories.Attendances,
  dateRange: EventSubCategories.DateRange,
  sessionTypes: EventSubCategories.SessionTypes,
  coachSessionStyle: EventSubCategories.CoachSessionStyle,
  memberName: EventSubCategories.MemberName,
  coachingExperienceType: EventSubCategories.CoachExperienceType,
};

export const getAttendanceOptions = ({
  includePersonalTraining,
}: {
  includePersonalTraining?: boolean;
}): {
  label: string;
  icon: IconDefinition;
  iconColor: string;
  testId: string;
  attendance: CoachSessionAttendance;
  eventLabel: EventLabels;
}[] => [
  {
    label: 'Attended',
    testId: 'attended-option',
    icon: faCircleCheck,
    iconColor: AQUAMARINE,
    attendance: CoachSessionAttendance.Attended,
    eventLabel: EventLabels.Attended,
  },
  {
    label: 'No Show',
    testId: 'noShow-option',
    icon: faBan,
    iconColor: STATUS_ERROR,
    attendance: CoachSessionAttendance.NoShow,
    eventLabel: EventLabels.NoShow,
  },
  ...(includePersonalTraining
    ? [
        {
          label: 'Excused Absence',
          icon: faCircleXmark,
          iconColor: PRIMARY_DISABLED,
          testId: 'excused-option',
          attendance: CoachSessionAttendance.Excused,
          eventLabel: EventLabels.ExcusedAbsence,
        },
      ]
    : []),
];

export const getSessionSubtypeOption = (
  subtype?: CoachSessionSubtype | null,
) => {
  switch (subtype) {
    case CoachSessionSubtype.MovementAssessment:
      return {
        label: 'Movement Assessment',
        value: CoachSessionSubtype.MovementAssessment,
      };
    case CoachSessionSubtype.ErgonomicEvaluation:
      return {
        label: 'Ergonomic Evaluation',
        value: CoachSessionSubtype.ErgonomicEvaluation,
      };
    case CoachSessionSubtype.BodyCompositionAssessment:
      return {
        label: 'Body Composition Assessment',
        value: CoachSessionSubtype.BodyCompositionAssessment,
      };
    case CoachSessionSubtype.NewMemberOrientation:
      return {
        label: 'New Member Orientation',
        value: CoachSessionSubtype.NewMemberOrientation,
      };
    default:
      return undefined;
  }
};
